
import { Vue, Options, prop } from "vue-class-component";
import { TrackDeliveryController } from "@/app/ui/controllers/TrackDeliveryController";
import { TrackDeliveryData } from "@/domain/entities/TrackDelivery";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  IS_SENDER_ACCOUNT,
  IS_ENABLE_CLAIM,
  IS_ENABLE_CLAIM_PENGIRIMAN_AKTIF,
  IS_ENABLE_CLAIM_RIWAYAT_PENGIRIMAN,
  checkRolePermission
} from "@/app/infrastructures/misc/Utils";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";
import { TRACK_DELIVERY } from "@/app/infrastructures/misc/RolePermission";
import FormClaim from "./components/form-claim.vue";
import { isCustomerApps, isProductAbleClaim, isSTTRetail } from "./modules";
import { GTMCommonEvent } from "@/app/infrastructures/misc/gtm-event/common-event";

class Prop {
  routerName = prop<string>({
    default: "pengiriman-aktif",
    type: String
  });
}

@Options({
  emits: ["fetchDataPackages"],
  components: {
    FormClaim
  },
  computed: {
    isDetailAble() {
      return IS_SENDER_ACCOUNT ?? checkRolePermission(TRACK_DELIVERY.DETAIL);
    }
  }
})
export default class InPackages extends Vue.with(Prop) {
  isSmallHeightScreen = false;

  get accountCurrency(): "IDR" | "MYR" {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  mounted() {
    this.setHeightScreen();

    window.addEventListener("resize", () => {
      this.setHeightScreen();
    });
  }

  setHeightScreen() {
    this.isSmallHeightScreen = window.innerHeight <= 600;
  }

  get isLoading() {
    return TrackDeliveryController.isLoading;
  }

  get isError() {
    return TrackDeliveryController.isError;
  }

  get errorCause() {
    return TrackDeliveryController.errorCause;
  }

  get dataPackages() {
    return TrackDeliveryController.trackDeliveryPackages.data;
  }

  get firstRequest() {
    return TrackDeliveryController.firstRequest;
  }

  get isAbleToClaimBasedOnEnv() {
    if (IS_ENABLE_CLAIM) {
      return true;
    } else if (this.$route.name === "pengiriman-aktif") {
      return IS_ENABLE_CLAIM_PENGIRIMAN_AKTIF;
    } else if (this.$route.name === "riwayat-pengiriman") {
      return IS_ENABLE_CLAIM_RIWAYAT_PENGIRIMAN;
    } else {
      return false;
    }
  }

  isClaimedAndClaimAble(isClaimed: boolean, isClaimAble: boolean) {
    return isClaimed && isClaimAble;
  }

  isNotClaimedAndClaimAble(isClaimed: boolean, isClaimAble: boolean) {
    return !isClaimed && isClaimAble;
  }

  get columns() {
    const tempColumns: Array<any> = [
      {
        name: this.$t("trackDelivery.columns.colSttNoResi"),
        key: "noResi",
        styleHead: "w-28 md:w-32 text-left whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          if (item.isCod) {
            return `
            <div>
              <img  class="w-6 lg:w-auto absolute top-0 left-0" src='${require("@/app/ui/assets/svg/cod-label.svg")}' alt="label-float" />
            </div>
            <div class="lg:pl-5 text-left text-black-lp-300">${
              item.noResi
            }</div>
          `;
          }
          return `<span class='text-left text-black-lp-300 flex justify-center'>${item.noResi}</span>`;
        }
      },
      {
        name: this.$t("trackDelivery.columns.colShipmentId"),
        key: "shipmentId",
        styleHead: "w-28 text-left whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          return `<span class='text-left text-black-lp-300 flex justify-center'>${item.shipmentId}</span>`;
        }
      },
      {
        name: this.$t("trackDelivery.columns.colPackageWeight"),
        key: "chargeableWeight",
        styleHead: "w-28 text-left whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          return `<span class='text-left text-black-lp-300 flex justify-center font-bold'>${item.chargeableWeight} Kg</span>`;
        }
      },
      {
        name: this.$t("trackDelivery.columns.colShippingCost"),
        key: "totalAmount",
        styleHead: "w-28 text-left whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          if (item.isPromoRetail) {
            return `<div>
              <span class='text-left text-gray-lp-600 flex justify-center font-normal line-through'>${formatPrice(
                item.totalTariffBeforeDiscount,
                "",
                this.accountCurrency
              )}</span>
              <span class='text-left text-black-lp-300 flex justify-center font-bold'>${formatPrice(
                item.totalTariffAfterDiscount,
                "",
                this.accountCurrency
              )}</span>
            </div>`;
          }
          return `<span class='text-left text-black-lp-300 flex justify-center font-bold'>${formatPrice(
            item.totalAmount,
            "",
            this.accountCurrency
          )}</span>`;
        }
      },
      {
        name: this.$t("trackDelivery.columns.colRecipientDetail"),
        key: "consigne",
        styleHead: "w-28 text-left whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          return `<div class='flex font-semibold md:font-normal text-black-lp-300 break-all'>${item.consigneName}</div>
        <div class='flex text-gray-lp-500 lg:text-14px'>${item.consigneAddress}</div>`;
        }
      },
      {
        name: this.$t("trackDelivery.columns.colPackageStatus"),
        key: "status",
        styleHead: "w-28 text-center whitespace-no-wrap",
        styleCustom: "text-10px xl:text-14px lg:text-12px",
        render: (item: TrackDeliveryData) => {
          if (item.status === "POD") {
            return `<div class="flex text-center font-medium capitalize">
                    <div class="rounded-full px-2 py-0 bg-green-lp-100 text-white">${item.status}</div>
                  </div>`;
          }
          return `<div class="flex font-medium text-center capitalize">
                    <div class="rounded-full px-2 py-0 bg-yellow-lp-100 text-white">${item.status}</div>
                </div>`;
        }
      }
    ];
    this.deliveryPerformanceColumn(tempColumns);
    if (
      this.isAbleToClaimBasedOnEnv &&
      this.routerName !== "performa-pengiriman"
    ) {
      this.ableToClaimBasedOnEnvColumn(tempColumns);
    }
    return tempColumns;
  }

  // make a claim
  isClaimForm = false;

  onClickRow(item: any) {
    this.$router.push(`/lacak-pengiriman/${item.noResi}`);
  }

  deliveryPerformanceColumn(column: Array<any>) {
    if (this.routerName === "performa-pengiriman") {
      column.push({
        name: this.$t("trackDelivery.columns.colDeliveryPerformance"),
        key: "status-performa",
        styleHead: "w-28 text-center whitespace-no-wrap",
        render: (item: TrackDeliveryData) => {
          const statusLabel =
            item.statusPerformance === "Terlambat"
              ? this.$t("trackDelivery.columns.statusLate")
              : this.$t("trackDelivery.columns.statusOnTime");
          if (item.statusPerformance === "Terlambat") {
            return `<div class="flex text-center font-medium capitalize">
                    <div class="rounded-full px-2 py-0 bg-pink-lp-100 text-white">${statusLabel}</div>
                  </div>`;
          }
          return `<div class="flex font-medium text-center capitalize">
                    <div class="rounded-full px-2 py-0 bg-green-lp-900 text-white">${statusLabel}</div>
                </div>`;
        }
      });
    }
  }

  ableToClaimBasedOnEnvColumn(column: Array<any>) {
    column.push(
      {
        name: this.$t("trackDelivery.columns.colClaimNo"),
        key: "noKlaim",
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: TrackDeliveryData) => {
          if (item.claimNo) {
            return `<span class='text-left text-red-lp-100 flex justify-center'>
              <a href='https://lionparcel.com/klaim/track' target='_blank'>${item.claimNo}</a>
            </span>`;
          }
          return `<span class='text-left flex justify-center'>-</span>`;
        },
        toolTip: (item: TrackDeliveryData) => {
          if (
            (isCustomerApps(item.shipmentId) || isSTTRetail(item.noResi)) &&
            isProductAbleClaim(item.product) &&
            this.isClaimedAndClaimAble(item.isClaimed, item.isClaimAble) &&
            item.claimNo
          ) {
            return `<div class='flex text-black-lp-300 text-sm'>${this.$t(
              "trackDelivery.columns.claimStatusTip"
            )}</div>`;
          }
        }
      },
      {
        name: this.$t("trackDelivery.columns.colClaimPackage"),
        key: "button_column",
        styleHead: "w-28 text-left",
        styleButton: (item: TrackDeliveryData) => {
          if (
            (isCustomerApps(item.shipmentId) || isSTTRetail(item.noResi)) &&
            isProductAbleClaim(item.product) &&
            this.isNotClaimedAndClaimAble(item.isClaimed, item.isClaimAble)
          ) {
            return {
              outline: true,
              title: this.$t("trackDelivery.columns.submitClaim"),
              textColor: "red-lp-100",
              color: "red-lp-100",
              class: "text-center",
              small: true,
              clickFunction: () => {
                this.getDetailStt(item.sttId, item.noResi);
              },
              chip: ""
            };
          } else if (
            this.isClaimedAndClaimAble(item.isClaimed, item.isClaimAble)
          ) {
            return {
              chip: `<div class="flex text-center font-medium capitalize">
                <div class="rounded-full px-2 py-0 bg-purple-lp-700 text-white">${this.$t(
                  "trackDelivery.columns.claimSubmitted"
                )}</div>
              </div>`
            };
          }
          return {
            chip: `<div>-</div>`
          };
        }
      }
    );
  }

  getDetailStt(id: number, sttNo = "") {
    this.$router.push(
      `/lacak-pengiriman/${id}/ajukan-klaim?track=${this.routerName}&type=paket-untuk-anda&sttNo=${sttNo}`
    );

    GTMCommonEvent("sender_ajukan_klaim_request");
  }

  get pagination() {
    return TrackDeliveryController.trackDeliveryPackages.pagination;
  }

  fetchDataPackages() {
    this.$emit("fetchDataPackages");
  }
}
